import React, { useCallback, useEffect, useMemo } from 'react';
import { Alert, Modal } from 'react-bootstrap';
import { ClinicalButton, ClinicalModalActions } from './ui';
import { MEDICAL_EXAM_CATEGORIES } from '../../constants/medicalExamCategories';
import {
  examMetaLine,
  formatConsultationDate,
  formatPrintDate,
  getCategoryLabelPlural
} from './utils/examOrderFormat';
import './ConsultationExamOrdersViewModal.css';

const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/logo.png`;
const TAB_ANALYSIS = MEDICAL_EXAM_CATEGORIES.ANALYSIS.id;
const TAB_RADIO = MEDICAL_EXAM_CATEGORIES.RADIOGRAPHY.id;
const TAB_ULTRA = MEDICAL_EXAM_CATEGORIES.ULTRASOUND.id;

const CATEGORY_COPY = {
  [TAB_ANALYSIS]: {
    modalTitle: 'Bon de demande — analyses',
    docTitle: "Bon de demande d'analyses médicales",
    foot: 'FUNTOA SMIE — document à remettre au laboratoire.'
  },
  [TAB_RADIO]: {
    modalTitle: 'Bon de demande — radiographies',
    docTitle: 'Bon de demande de radiographies',
    foot: 'FUNTOA SMIE — document à remettre au service de radiologie.'
  },
  [TAB_ULTRA]: {
    modalTitle: 'Bon de demande — échographies',
    docTitle: 'Bon de demande d’échographies',
    foot: 'FUNTOA SMIE — document à remettre au service d’échographie.'
  }
};

function ExamList({ rows }) {
  if (!rows.length) {
    return <p className="eo-simple__none">— Aucun examen —</p>;
  }

  return (
    <ol className="eo-simple__list">
      {rows.map((row, index) => {
        const meta = examMetaLine(row);
        return (
          <li key={row.catalog_exam_id || index} className="eo-simple__item">
            <span className="eo-simple__exam">{row.exam_name}</span>
            {meta ? <div className="eo-simple__sub">{meta}</div> : null}
          </li>
        );
      })}
    </ol>
  );
}

export default function ConsultationExamOrdersViewModal({
  show,
  onHide,
  consultation,
  orders = [],
  category = TAB_ANALYSIS,
  clinicalInformation = '',
  previewUnsaved = false
}) {
  const copy = CATEGORY_COPY[category] || CATEGORY_COPY[TAB_ANALYSIS];
  const sectionTitle = getCategoryLabelPlural(category);

  const filteredOrders = useMemo(
    () =>
      orders.filter(
        (o) => String(o.category || '').toUpperCase() === String(category || '').toUpperCase()
      ),
    [orders, category]
  );

  const clinicalText = (clinicalInformation || '').trim();

  const runPrint = useCallback(() => {
    document.body.classList.add('exam-order-print-mode');
    const cleanup = () => {
      document.body.classList.remove('exam-order-print-mode');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.setTimeout(() => window.print(), 150);
  }, []);

  useEffect(() => {
    if (!show) {
      document.body.classList.remove('exam-order-print-mode');
    }
  }, [show]);

  const patientName = (consultation?.patient_name || '').trim() || '—';
  const doctorName = (consultation?.doctor_name || '').trim() || '—';

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      scrollable
      className="exam-order-view-modal clinical-view-modal"
      aria-labelledby="exam-order-view-title"
    >
      <Modal.Header closeButton className="exam-order-view-modal__head recv-no-print">
        <div className="d-flex align-items-center gap-2">
          <img src={LOGO_SRC} alt="" className="exam-order-view-modal__head-logo" />
          <Modal.Title id="exam-order-view-title" className="fs-6 mb-0">
            {copy.modalTitle}
          </Modal.Title>
        </div>
      </Modal.Header>

      <Modal.Body className="exam-order-view-modal__body">
        <ClinicalModalActions>
          <ClinicalButton variant="secondary" size="sm" onClick={runPrint}>
            Imprimer
          </ClinicalButton>
          <ClinicalButton variant="primary" size="sm" icon="download" onClick={runPrint}>
            PDF
          </ClinicalButton>
          <span className="small text-muted">
            PDF → « Enregistrer au format PDF » dans la fenêtre d&apos;impression.
          </span>
        </ClinicalModalActions>

        {previewUnsaved ? (
          <Alert variant="warning" className="py-2 small recv-no-print">
            Aperçu non enregistré — enregistrez la demande avant impression officielle.
          </Alert>
        ) : null}

        <article className="eo-simple" id="exam-order-print-sheet">
          <header className="eo-simple__header">
            <img src={LOGO_SRC} alt="FUNTOA SMIE" className="eo-simple__logo" />
            <div>
              <h1 className="eo-simple__brand">FUNTOA SMIE</h1>
              <p className="eo-simple__doctype">{copy.docTitle}</p>
            </div>
          </header>

          <p className="eo-simple__meta">
            Consultation n° <strong>{consultation?.id ?? '—'}</strong>
            {' · '}
            {formatConsultationDate(consultation)}
            {' · '}
            édité le {formatPrintDate()}
          </p>

          <div className="eo-simple__info">
            <p>
              <strong>Patient :</strong> {patientName}
              {consultation?.patient_age != null ? `, ${consultation.patient_age} ans` : ''}
            </p>
            {consultation?.patient_phone ? (
              <p>
                <strong>Téléphone :</strong> {consultation.patient_phone}
              </p>
            ) : null}
          </div>

          <div className="eo-simple__clinical">
            <p className="eo-simple__clinical-label">Renseignement clinique</p>
            <p className="eo-simple__clinical-text">{clinicalText || '—'}</p>
          </div>

          <div className="eo-simple__block">
            <h2 className="eo-simple__heading">{sectionTitle}</h2>
            <ExamList rows={filteredOrders} />
          </div>

          <div className="eo-simple__sign">
            <p>Signature du médecin</p>
            <div className="eo-simple__sign-line">&nbsp;</div>
          </div>

          <p className="eo-simple__foot">{copy.foot}</p>
          {doctorName !== '—' ? (
            <p className="eo-simple__doctor-quiet">Médecin prescripteur : {doctorName}</p>
          ) : null}
        </article>
      </Modal.Body>

      <Modal.Footer className="recv-no-print">
        <ClinicalButton variant="ghost" size="sm" onClick={onHide}>
          Fermer
        </ClinicalButton>
        <ClinicalButton variant="view" size="sm" onClick={runPrint}>
          Imprimer
        </ClinicalButton>
        <ClinicalButton variant="primary" size="sm" icon="download" onClick={runPrint}>
          Exporter en PDF
        </ClinicalButton>
      </Modal.Footer>
    </Modal>
  );
}
